<script setup lang="ts">
import IconLucide from '@/components/icons/IconLucide.vue'
import { useAuthStore, useSavingStore, useUtilsStore } from '@/stores'
import { computed, onMounted, ref } from 'vue'
import type { Header } from 'vue3-easy-data-table'

const useUtils = useUtilsStore()
const useAuth = useAuthStore()
const useSaving = useSavingStore()
const user = computed(() => useAuth.user)
const saldo = computed(() => useSaving.saldo)
const isLoading = computed(() => useUtils.isLoading)
const isShow = ref(false)
const isOnline = computed(() => useUtils.isOnline)

const searchField = ref('')
const clearDate = () => {
  searchField.value = ''
}

onMounted(() => {
  useUtils.toogleLoading(true)
  useSaving.getSaving().then(() => {
    useUtils.toogleLoading(false)
  })
  useSaving.getInvoice()
})

// Contoh kolom dan data untuk tabel
const headers: Header[] = [
  { text: 'Invoice', value: 'invoice' },
  { text: 'Nama Invoice', value: 'invoice_name' },
  { text: 'Tanggal', value: 'date' },
  { text: 'Total', value: 'amount' },
  { text: 'Kategori', value: 'category' },
  { text: 'Status', value: 'status' },
  { text: 'Admin', value: 'admin' },
]

const items = computed(() => useSaving.invoice)
</script>

<template>
  <main class="grid grid-cols-12 gap-5">
    <div class="col-span-12 space-y-3">
      <h1 class="text-2xl font-bold">
        Hey {{ user.name }}, Kamu sedang
        <span
          :class="isOnline ? 'badge badge-success' : 'badge badge-error'"
          class="ml-2 align-middle"
        >
          {{ isOnline ? 'Online' : 'Offline' }}
        </span>
      </h1>
      <div class="flex justify-between items-center">
        <p class="text-sm font-light">Informasi Saldo</p>
        <button @click="isShow = !isShow" class="btn btn-sm btn-info">
          Lihat Saldo
          <IconLucide v-if="isShow" name="Eye" class="w-6 h-6" />
          <IconLucide v-else name="EyeOff" class="w-6 h-6" />
        </button>
      </div>
    </div>
    <!-- stat saldo -->
    <div
      class="stats border border-base-content/10 perfect-scrollbar shadow-sm border-b-3 col-span-12 lg:col-span-6 2xl:col-span-3 bg-base-100"
    >
      <div class="stat perfect-scrollbar">
        <div class="stat-figure text-success">
          <IconLucide name="WalletMinimal" class="size-8" />
        </div>
        <div class="stat-title">Saldo Iuran Sukarela</div>
        <div
          :class="[
            'stat-value transition-all duration-500 ease-in-out',
            isShow ? 'text-success' : 'text-base-content',
          ]"
        >
          <span v-if="isLoading" class="loading loading-bars"></span>
          <span v-else>
            {{ isShow ? saldo?.saldo_sukarela : '*'.repeat(String(saldo?.saldo_sukarela).length) }}
          </span>
        </div>
      </div>
    </div>
    <div
      class="stats border border-base-content/10 shadow-sm border-b-3 col-span-12 lg:col-span-6 2xl:col-span-3 bg-base-100"
    >
      <div class="stat">
        <div class="stat-figure text-success">
          <IconLucide name="WalletMinimal" class="size-8" />
        </div>
        <div class="stat-title">Saldo Iuran Wajib</div>
        <div
          :class="[
            'stat-value transition-all duration-500 ease-in-out',
            isShow ? 'text-success' : 'text-base-content',
          ]"
        >
          <span v-if="isLoading" class="loading loading-bars"></span>
          <span v-else>
            {{ isShow ? saldo?.saldo_wajib : '*'.repeat(String(saldo?.saldo_wajib).length) }}
          </span>
        </div>
      </div>
    </div>
    <div
      class="stats border border-base-content/10 shadow-sm border-b-3 col-span-12 lg:col-span-6 2xl:col-span-3 bg-base-100"
    >
      <div class="stat">
        <div class="stat-figure text-success">
          <IconLucide name="WalletCards" class="size-8" />
        </div>
        <div class="stat-title">Saldo Investasi</div>
        <div
          :class="[
            'stat-value transition-all duration-500 ease-in-out',
            isShow ? 'text-success' : 'text-base-content',
          ]"
        >
          <span v-if="isLoading" class="loading loading-bars"></span>
          <span v-else>
            {{
              isShow ? saldo?.saldo_investasi : '*'.repeat(String(saldo?.saldo_investasi).length)
            }}
          </span>
        </div>
      </div>
    </div>
    <div
      class="stats border border-base-content/10 shadow-sm border-b-3 col-span-12 lg:col-span-6 2xl:col-span-3 bg-base-100"
    >
      <div class="stat">
        <div class="stat-figure text-success">
          <IconLucide name="Wallet" class="size-8" />
        </div>
        <div class="stat-title">Total Saldo</div>
        <div
          :class="[
            'stat-value transition-all duration-500 ease-in-out',
            isShow ? 'text-success' : 'text-base-content',
          ]"
        >
          <span v-if="isLoading" class="loading loading-bars"></span>
          <span v-else>
            {{ isShow ? saldo?.saldo_total : '*'.repeat(String(saldo?.saldo_total).length) }}
          </span>
        </div>
      </div>
    </div>
    <!-- end stat saldo -->

    <div class="col-span-12">
      <label class="input mb-3">
        <button class="label" @click="clearDate">
          <IconLucide v-if="searchField" name="X" class="w-5 h-5" />
          <IconLucide v-else name="Filter" class="w-5 h-5" />
        </button>
        <input type="date" v-model="searchField" />
      </label>
      <EasyDataTable
        :headers="headers"
        :items="items"
        table-class-name="customize-table text-base-content"
        :search-value="searchField"
      >
        <template #item-invoice="{ invoice, uuid }">
          <router-link
            :to="{ name: 'invoice-detail', params: { code: uuid } }"
            class="btn btn-primary btn-sm"
          >
            {{ invoice }}
          </router-link>
        </template>
        <template #item-status="{ status }">
          <span
            :class="[
              'badge badge-soft capitalize',
              status == 'dibayar' ? 'badge-success' : 'badge-error',
            ]"
          >
            {{ status }}
          </span>
        </template>
        <template #item-admin="{ admin }">
          <span class="badge badge-soft capitalize badge-info"> {{ admin }} </span>
        </template>
      </EasyDataTable>
    </div>
  </main>
</template>
